import React, { Component } from 'react';
import axios from "axios";

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import styles from './overview.module.css';
import SpinnerModal from '@components/SpinnerModal';
import BarChartWidget from '@components/BarChartWidget';
import StackedBarChartWidget from '@components/StackedBarChartWidget';
import PieChartWidget from '@components/PieChartWidget';
import SurveyService from '@services/SurveyService';
import DataCaptureService from '@services/DataCaptureService';
import RespondentDashboardService from '@services/RespondentDashboardService';

import Chip from '@mui/material/Chip';
import HomeIcon from '@mui/icons-material/Home';

import Alert from '@mui/material/Alert';

class Dashboard extends Component {

    state = {
        loading: false,
        hasDataToDisplay: true,
        organisationStatus: null,
        areasOfInterest: [],
        sourcesOfFunds: [],
        capitalExpenditures: [],
        provincialExpenditures: [],
        internationalSourcesOfFunds: [],
        categoriesofRnDExpenditure: [],
        specificAreasOfInterest: [],
        multiDisciplinaryAreasOfInterest: [],
        surveyId: 0,
        internationalSourcesOfFundsJSON: [],
        respondentCapitalExpenditureJSON: [],
        provincialExpendituresJSON: [],
        categoriesofRnDExpenditureJSON: [],
        organisationName: null,
        headcounts: [],
        updated_headcounts: [],
        ftecounts: [],
        surveyCycles: [],
        surveyCycleId: process.env.REACT_APP_CURRENT_SURVEY_CYCLE
    }

    handleSurveyCycleChange = (event) => {
        this.setState({ surveyCycleId: (event.target.value) });

        this.componentDidMount();

    };

    async componentDidMount() {
        this.setState({ loading: true });
        const HE_SECTOR = 3;

        function parseJwt(token) {
            const base64Payload = token.split('.')[1];
            const payload = atob(base64Payload);
            return JSON.parse(payload.toString());
        }

        let bearer = localStorage.getItem('Bearer');

        if (!bearer) {
            window.location = "/login";

        }


        let payload = parseJwt(bearer);

        let organisationId = payload.org_id;

        let sectorId = payload.sector_id;

        const surveyCycleId = process.env.REACT_APP_CURRENT_SURVEY_CYCLE;
        localStorage.setItem('SurveyCycleId', surveyCycleId);

        const transformRawFTEDataForStackedChart = (rawFTEData) => {

            let filteredData = [];

            if (parseFloat(sectorId) === parseFloat(HE_SECTOR)) {
                // Apply only to HE sector
                filteredData = rawFTEData.filter((item) => {
                    const totalCount = (item.dimensions.toUpperCase().match(/TOTAL/g) || []).length; //
                    return totalCount > 1 && !item.dimensions.toUpperCase().includes('MALE');
                }).map((item) => {
                    return { dimensions: item.dimensions.split(';').reverse().join(';'), value: item.floatMeasure };
                });
            } else {
                // Apply to all NON-HE sectors
                filteredData = rawFTEData.filter((item) => {
                    return !item.dimensions.toUpperCase().includes('CALCULATED')
                        && !item.dimensions.toUpperCase().includes('MALE')
                        && !item.dimensions.toUpperCase().includes('AVERAGE');
                }).map((item) => {
                    return { dimensions: item.dimensions, value: item.floatMeasure };
                });
            }

            // Determine the categories, those are dimensions without the word 'total' in them
            const category_list = [];
            filteredData.forEach(item => {
                const parts = item.dimensions.split(';');
                parts.forEach(part => {
                    const trimmedPart = part.trim();
                    if (!trimmedPart.toUpperCase().includes('TOTAL')) {
                        if (!category_list.includes(trimmedPart)) {
                            category_list.push(trimmedPart);
                        }
                    }
                });
            });

            const transformed = filteredData.map(item => {
                const parts = item.dimensions.split(';');
                const commonElements = parts.filter(item => category_list.includes(item));

                if (commonElements.length === 0) {
                    // If no common elements, return original array
                    return parts;
                }

                // Get elements that are not in category_list
                const otherElements = parts.filter(item => !category_list.includes(item));

                // If we have enough other elements, split them between before and after
                const middleIndex = Math.floor(otherElements.length / 2);
                const beforeElements = otherElements.slice(0, middleIndex);
                const afterElements = otherElements.slice(middleIndex);

                // Combine the arrays with common elements in the middle
                item.dimensions = [...beforeElements, ...commonElements, ...afterElements].reverse();

                return {
                    category: item.dimensions[1].trim(),
                    subcategory: item.dimensions[0] ? item.dimensions[0].trim() : 'Other',
                    value: item.value
                };
            });

            // Group by subcategories to create series
            const series = [];
            const categories = [...new Set(transformed.map(item => item.category))];
            const subcategories = [...new Set(transformed.map(item => item.subcategory))];

            // Create series for each subcategory
            subcategories.forEach(subcategory => {
                const seriesData = categories.map(category => {
                    const match = transformed.find(item =>
                        item.category === category &&
                        item.subcategory === subcategory
                    );
                    return match ? match.value : 0;
                });

                series.push({
                    label: subcategory,
                    dataSet: seriesData
                });
            });

            return {
                labels: categories,
                datasets: series
            };
        };

        const transformRawHeadcountForStackedChart = (rawData) => {
            // Define race and gender terms to exclude
            const raceGenderTerms = ['African', 'White', 'Indian', 'Coloured', 'Male', 'Female'];
            
            // Filter data: exclude race/gender rows and rows with 'TOTAL' in dimensions
            const filteredData = rawData
                .filter(item => {
                    const dimensions = item.dimensions.toUpperCase();
                    
                    // Exclude rows with TOTAL in dimensions
                    if (dimensions.includes(' TOTAL')) return false;
                    
                    // Exclude rows with race/gender terms
                    const parts = item.dimensions.split(';');
                    const hasRaceGender = parts.some(part => 
                        raceGenderTerms.includes(part.trim())
                    );
                    
                    return !hasRaceGender;
                })
                .map(item => {
                    const parts = item.dimensions.split(';');
                    
                    const role = parts.find(part => 
                        part.includes('Researchers') || 
                        part.includes('Technicians') || 
                        part.includes('Other Personnel')
                    )?.trim();
                    
                    const education = parts.find(part => 
                        part.includes('Doctoral') || 
                        part.includes('Masters') || 
                        part.includes('Diplomas')
                    )?.trim();
                    
                    // Replace null floatMeasure with 0
                    const value = item.floatMeasure || 0;
                    
                    return { role, education, value };
                })
                .filter(item => item.role && item.education);

            // X-axis: Roles, Y-axis stacked: Education levels (ordered)
            const roles = [...new Set(filteredData.map(item => item.role))];
            const educationLevels = [
                'Doctoral Degree or Equivalent',
                'Masters, Hons, Bachelor or equivalant', 
                'Diplomas and Other Qualifications'
            ];

            const datasets = educationLevels.map(education => {
                const dataSet = roles.map(role => {
                    const match = filteredData.find(item => 
                        item.role === role && item.education === education
                    );
                    return match ? match.value : 0;
                });

                return {
                    label: education,
                    dataSet
                };
            });

            return {
                labels: roles,
                datasets
            };
        };

        const questionnaireId = await (async () => {
            const result = await SurveyService.getQuestionnaireIdBySurveyCycleIdAndSectorId(surveyCycleId, sectorId);
            return result;
        })([]);

        if (questionnaireId !== undefined && questionnaireId !== null) {
            localStorage.setItem('SurveyId', questionnaireId);
            this.setState({ surveyId: questionnaireId });
        }

        const thisOrganisationStatus = await (async () => {
            const organisationStatus = await DataCaptureService.getOrganisationStatusBySurveyCycleIdAndOrganisationId(this.state.surveyCycleId, organisationId);
            return organisationStatus;
        })([]);
        this.setState({ organisationStatus: thisOrganisationStatus });

        if (thisOrganisationStatus !== undefined && thisOrganisationStatus !== null) {
            if (thisOrganisationStatus.datacapture1Completed !== undefined && thisOrganisationStatus.datacapture1Completed !== null && thisOrganisationStatus.datacapture1Completed === true &&
                thisOrganisationStatus.datacapture2Completed !== undefined && thisOrganisationStatus.datacapture2Completed !== null && thisOrganisationStatus.datacapture2Completed === true &&
                thisOrganisationStatus.nilReturn !== undefined && thisOrganisationStatus.nilReturn !== null && thisOrganisationStatus.nilReturn !== true) {

                this.setState({ hasDataToDisplay: true });

                const respondentInternationalSourcesOfFunds = await (async () => {
                    const result = await RespondentDashboardService.getOrganisationInternationalSourcesofFunds(organisationId, this.state.surveyCycleId);
                    return result;
                })([]);
                this.setState({ internationalSourcesOfFundsJSON: respondentInternationalSourcesOfFunds });


                const respondentCapitalExpenditure = await (async () => {
                    const result = await RespondentDashboardService.getOrganisationCapitalExpenditure(organisationId, this.state.surveyCycleId);
                    return result;
                })([]);
                this.setState({ respondentCapitalExpenditureJSON: respondentCapitalExpenditure });

                const respondentProvincialExpenditure = await (async () => {
                    const result = await RespondentDashboardService.getOrganisationProvincialExpenditure(organisationId, this.state.surveyCycleId);
                    return result;
                })([]);
                this.setState({ provincialExpendituresJSON: respondentProvincialExpenditure });

                const respondentCategoriesofRnDExpenditure = await (async () => {
                    const result = await RespondentDashboardService.getOrganisationCategoriesOfExpenditure(organisationId, this.state.surveyCycleId);
                    return result;
                })([]);
                this.setState({ categoriesofRnDExpenditureJSON: respondentCategoriesofRnDExpenditure });

                const respondentHeadCounts = await (async () => {
                    const result = await RespondentDashboardService.getOrganisationHeadCounts(organisationId, this.state.surveyCycleId);
                    return result;
                })([]);
                this.setState({ headcounts: respondentHeadCounts });

                const NEWRespondentHeadCounts = await (async () => {
                    const result = await RespondentDashboardService.getQuestionFactsByOrganisationIdAndSurveyCycleAndQuestionId(organisationId, this.state.surveyCycleId, 41);

                    const chartData = transformRawHeadcountForStackedChart(result);

                    return chartData;
                })([]);
                this.setState({ updated_headcounts: NEWRespondentHeadCounts });

                const respondentFTECounts = await (async () => {
                    const rawFTEdata = await RespondentDashboardService.getQuestionFactsByOrganisationIdAndSurveyCycleAndQuestionId(organisationId, this.state.surveyCycleId, 40);

                    const chartData = transformRawFTEDataForStackedChart(rawFTEdata);

                    return chartData;
                })([]);
                this.setState({ ftecounts: respondentFTECounts });
            } else {
                this.setState({ hasDataToDisplay: false });
            }
        } else {
            this.setState({ hasDataToDisplay: false });
        }

        let surveyCycles = process.env.REACT_APP_RESPONDENT_DASHBOARD_MICROSERVICE_URL + process.env.REACT_APP_SERVICES_VERSION + `/respondentdashboardservice/surveycycles`
        let surveyCyclesURL = axios.create({ baseURL: surveyCycles });

        surveyCyclesURL.get('').then(
            resp => {
                const respData = resp.data;
                let surveysResponse = [];

                if (respData.status === "SUCCESS") {
                    if (respData.response.success != null) {
                        respData.response.success.forEach(record => {
                            surveysResponse.push(record);

                        });
                    }
                }

                this.setState({
                    surveyCycles: surveysResponse,
                });
            }
        );

        let organisationNameUrl = process.env.REACT_APP_SURVEY_MICROSERVICE_URL + process.env.REACT_APP_SERVICES_VERSION + "/questionnaireservice/questionnairetrackers/organisation/name/" + organisationId;
        let sectorNameUrl = process.env.REACT_APP_SURVEY_MICROSERVICE_URL + process.env.REACT_APP_SERVICES_VERSION + "/questionnaireservice/questionnairetrackers/sector/name/" + sectorId;

        if (organisationId) {

            axios.get(organisationNameUrl)
                .then(response => {
                    localStorage.setItem('organisationName', response.data.name);
                });
        }

        if (sectorId) {

            axios.get(sectorNameUrl)
                .then(response => {
                    localStorage.setItem('sectorName', response.data.name);
                });
        }

        this.setState({ loading: false });
    }

    render() {
        if (this.state.loading === true) {
            return <SpinnerModal settings={true} />
        } else {
            let surveyCycles = this.state.surveyCycles;
            
            return (
                <div className={styles.dashboard_overview_container}>
                    <div className={styles.breadcrumbs}>
                        <Chip variant='outlined' icon={<HomeIcon />}
                            label="Data Overview"
                        />
                    </div>
                    <div className={styles.flex_row_container}>
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                            <InputLabel id="survey-cycle-select-label">Survey Cycle</InputLabel>
                            <Select
                                labelId="survey-cycle-select-label"
                                id="survey-cycle-select"
                                value={this.state.surveyCycleId}
                                label="Survey Cycle"
                                onChange={this.handleSurveyCycleChange}
                            >
                                {surveyCycles.map(surveyCycle => {
                                    return (
                                        <MenuItem value={surveyCycle.Name}>{surveyCycle.Value}</MenuItem>
                                    );


                                })}


                            </Select>
                        </FormControl>
                    </div>
                    <br />
                    <br />
                    {(this.state.hasDataToDisplay !== true)
                        ? <div className={styles.featuredNoDataAlert}>
                            <Alert variant="outlined" severity="warning">{'No data has been captured for the selected survey cycle.'}</Alert>
                        </div>
                        : <div className={styles.dashboard_body_container}>
                            <div className={styles.flex_row_container}>
                                <BarChartWidget
                                    chartType="doughnut"
                                    headerTitle="R&D Capital Expenditure (R)"
                                    dataLabel="Rands"
                                    toolTip="Your organisation’s spend on key R&D capital items"
                                    headerTitleYear=" "
                                    dateToolTip="Charts display data only when survey for reference year is submitted."
                                    data={this.state.respondentCapitalExpenditureJSON}
                                    fillColor="orange"
                                    dataKey="Value"
                                />

                                <PieChartWidget
                                    chartType="doughnut"
                                    headerTitle="Categories of R&D Expenditure (%)"
                                    dataLabel="%"
                                    data={this.state.categoriesofRnDExpenditureJSON}
                                    toolTip="Your organisation’s breakdown of R&D types"
                                />
                            </div>

                            {/* <div className={styles.flex_row_container}>
                                <BarChartWidget
                                    headerTitle="Head Counts"
                                    dataLabel="Head Count"
                                    data={this.state.headcounts}
                                    legendPosition={"left"}
                                    toolTip="Your organisation’s headcount of R&D personnel including researchers, technicians and other support staff directly supporting R&D either part- or full-time."
                                />

                                <BarChartWidget
                                    headerTitle="Full-time Equivalent"
                                    dataLabel="FTE Count"
                                    data={this.state.ftecounts}
                                    legendPosition={"left"}
                                    toolTip="Your organisation’s full-time equivalent R&D personnel including researchers, technicians and other support staff directly supporting R&D"
                                />
                            </div> */}

                            <div className={styles.flex_row_container}>
                                <StackedBarChartWidget
                                    headerTitle="Head Counts Categories"
                                    dataLabel="Head Count Categories"
                                    data={this.state.updated_headcounts}
                                    xstacked={false}
                                    ystacked={false}
                                    legendPosition={"bottom"}
                                    toolTip="Your organisation’s headcount of R&D personnel including researchers, technicians and other support staff directly supporting R&D either part- or full-time."
                                />

                                <StackedBarChartWidget
                                    headerTitle="Headcount vs Full-time Equivalent"
                                    dataLabel="FTE Count"
                                    data={this.state.ftecounts}
                                    legendPosition={"bottom"}
                                    toolTip="Your organisation’s full-time equivalent R&D personnel including researchers, technicians and other support staff directly supporting R&D"
                                />
                            </div>

                            <div className={styles.flex_row_container}>
                                <BarChartWidget
                                    headerTitle="Provincial Capital Expenditure (%)"
                                    dataLabel="%"
                                    data={this.state.provincialExpendituresJSON}
                                    toolTip="Your organisation’s geographic breakdown of R&D capital expenditure"
                                />

                                <BarChartWidget
                                    headerTitle="International Sources of Funds (%)"
                                    dataLabel="%"
                                    data={this.state.internationalSourcesOfFundsJSON}
                                    toolTip="Your organisation’s percentage breakdown of R&D funding by region of the world"
                                />
                            </div>

                        </div>
                    }
                </div>
            )
        }
    }
}

export default Dashboard

